import React, { useEffect, useState } from "react";
import { Row, Col, Card, Tabs, Dropdown, Menu, Tooltip, Spin, Empty, message, Image, Button } from "antd";
import { MoreOutlined, DeleteOutlined, CheckCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import AdminMainLayout from "../AdminMainLayout";
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import "./AIImageLibrary.scss";

const { TabPane } = Tabs;

const AIImageLibrary = (props) => {
  const [submittedImages, setSubmittedImages] = useState([]);
  const [approvedImages, setApprovedImages] = useState([]);
  const [loadingSubmitted, setLoadingSubmitted] = useState(false);
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const fetchImages = async (status, setState, setLoading) => {
    try {
      setLoading(true);
      const payload = {
        action: "get_ai_image_public_library",
        status,
      };
      const res = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);
      const data = res.data;
      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : Array.isArray(data?.images)
        ? data.images
        : Array.isArray(data?.rows)
        ? data.rows
        : [];
      setState(list);
    } catch (error) {
      console.error("Error fetching AI image public library:", error);
      message.error("Failed to load AI Image Library data");
    } finally {
      setLoading(false);
    }
  };

  const refreshSubmitted = () => fetchImages("submitted", setSubmittedImages, setLoadingSubmitted);
  const refreshApproved = () => fetchImages("approved", setApprovedImages, setLoadingApproved);

  useEffect(() => {
    refreshSubmitted();
    refreshApproved();
  }, []);

  const buildFullUrl = (uriLike) => {
    if (!uriLike) return "";
    const src = String(uriLike);
    if (src.startsWith("http://") || src.startsWith("https://")) {
      return src;
    }
    // Fallback to default base URL (same as ActivityAndExploreSection)
    return ENVIRONMENT.getBaseURL() + src;
  };

  const getThumbnailUrl = (item) => {
    const rawUri = item.uri || item.image_uri || item.thumbnail;
    if (!rawUri) return "";
    let thumb = item.thumbnail || rawUri;
    // Derive 512px thumbnail from full-size uri if explicit thumbnail is not provided
    if (!item.thumbnail) {
      thumb = String(thumb).replace(/(\.[^.]+)$/, "_512$1");
    }
    return buildFullUrl(thumb);
  };

  const getFullImageUrl = (item) => {
    const rawUri =
      item.uri ||
      item.image_uri ||
      item.selectedImageUrl ||
      item.src ||
      item.url ||
      item.imageUrl;
    return buildFullUrl(rawUri);
  };

  const getTitle = (item) => {
    return item.display_name || item.title || item.name || `Render #${item.render_id || item.id || ""}`;
  };

  const handleUpdateStatus = async (item, status, isSubmittedTab) => {
    if (!item.id) {
      message.error("Missing id for this record");
      return;
    }
    setUpdatingId(item.id);
    try {
      const payload = {
        action: "update_ai_image_public_library_status",
        id: item.id,
        status,
      };
      await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);
      message.success(
        status === "approved"
          ? "Image approved successfully"
          : status === "deleted"
          ? "Image deleted successfully"
          : "Status updated"
      );
      if (isSubmittedTab) {
        refreshSubmitted();
        if (status === "approved") {
          refreshApproved();
        }
      } else {
        refreshApproved();
      }
    } catch (error) {
      console.error("Error updating AI image public library status:", error);
      message.error("Failed to update image status");
    } finally {
      setUpdatingId(null);
    }
  };

  const renderMenu = (item, isSubmittedTab) => {
    const menuItems = [];

    if (isSubmittedTab) {
      menuItems.push(
        <Menu.Item key="approve" onClick={() => handleUpdateStatus(item, "approved", true)}>
          <CheckCircleOutlined className="mr-8" />
          Approve
        </Menu.Item>
      );
    }

    menuItems.push(
      <Menu.Item key="delete" onClick={() => handleUpdateStatus(item, "deleted", isSubmittedTab)}>
        <DeleteOutlined className="mr-8" />
        Delete
      </Menu.Item>
    );

    return <Menu>{menuItems}</Menu>;
  };

  const renderGrid = (data, loading, isSubmittedTab) => {
    if (loading) {
      return (
        <div className="ai-image-library-loader">
          <Spin size="large" />
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="ai-image-library-empty">
          <Empty description="No images found" />
        </div>
      );
    }

    return (
      <>
        <Row gutter={[16, 16]}>
          {data.map((item) => {
            const thumbUrl = getThumbnailUrl(item);
            const fullUrl = getFullImageUrl(item);
            return (
              <Col span={8} key={item.id || `${fullUrl}-${item.render_id}`}>
                <Card
                  hoverable
                  className="ai-image-library-card"
                  bodyStyle={{ padding: 0 }}
                  onClick={() => {
                    if (fullUrl) {
                      setPreviewImage(fullUrl);
                      setPreviewVisible(true);
                    }
                  }}
                >
                  <div className="ai-image-library-card-image-wrapper">
                    <Image
                      src={thumbUrl || fullUrl}
                      alt={getTitle(item)}
                      className="ai-image-library-card-image"
                      preview={false}
                    />
                    <div
                      className="ai-image-library-card-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Dropdown
                        overlay={renderMenu(item, isSubmittedTab)}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <MoreOutlined className="ai-image-library-more-icon" />
                      </Dropdown>
                    </div>
                  </div>
                  <div className="ai-image-library-card-footer">
                    <Tooltip
                      title={
                        <span className="manrope f-12 white">
                          {getTitle(item)}
                        </span>
                      }
                    >
                      <div className="ai-image-library-card-title manrope f-14 w-500 lh-20">
                        {getTitle(item)}
                      </div>
                    </Tooltip>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
        {previewImage && (
          <Image
            style={{ display: "none" }}
            src={previewImage}
            preview={{
              visible: previewVisible,
              src: previewImage,
              onVisibleChange: (visible) => setPreviewVisible(visible),
            }}
          />
        )}
      </>
    );
  };

  return (
    <AdminMainLayout selected={"19"} color={true}>
      <div className="ai-image-library-page">
        <div className="ai-image-library-header">
          <div>
            <h2 className="manrope f-20 w-600 black-085 mb-4">AI Image Library</h2>
            <p className="manrope f-14 w-400 gray-045">
              Review and manage AI images submitted to the public library.
            </p>
          </div>
          <div className="ai-image-library-header-actions">
            <Tooltip title={<span className="manrope f-12 white">Refresh</span>}>
              <Button
                className="manrope f-14 w-500 lh-22 ai-action-btn pd-small"
                icon={<ReloadOutlined />}
                onClick={() => {
                  refreshSubmitted();
                  refreshApproved();
                }}
              >
                Refresh
              </Button>
            </Tooltip>
          </div>
        </div>

        <Tabs defaultActiveKey="submitted" className="ai-image-library-tabs">
          <TabPane tab="Submitted" key="submitted">
            {renderGrid(submittedImages, loadingSubmitted, true)}
          </TabPane>
          <TabPane tab="Approved" key="approved">
            {renderGrid(approvedImages, loadingApproved, false)}
          </TabPane>
        </Tabs>
      </div>
    </AdminMainLayout>
  );
};

export default AIImageLibrary;

